// The Microsoft session a Capacitor build holds, which is not the one MSAL manages.
//
// MSAL's redirect and popup flows don't work under the WebView schemes (capacitor:// on iOS,
// https://localhost on Android), so native sign-in runs the OAuth2 authorization code flow with
// PKCE by hand. Its tokens are minted by us and never enter MSAL's account cache, which makes
// this module the single owner of a native Microsoft session: how it is started, completed,
// stored, renewed, read and cleared. Everything else (apiAuth, outlookCalendar, AuthProvider)
// goes through the exported functions and knows none of the above.
//
// Two things about the mechanics are worth knowing before changing any of it.
//
// The code-for-token exchange goes over CapacitorHttp, not fetch. Entra returns CORS headers on
// its token endpoint only for redirect URIs registered under the *Single-page application*
// platform, and an SPA redirect URI has to be https — which a msauth:// custom scheme can never
// be. A request issued by the WebView is therefore refused before it starts. CapacitorHttp
// performs the request in the native layer, where there is no origin and so no CORS at all,
// which is what lets the flow keep the custom-scheme redirect URI the app already uses.
//
// The refresh token is what makes a native session survivable. The implicit flow this replaces
// (response_type=id_token token) got none — Entra issues no refresh token there whatever scopes
// are asked for, `offline_access` included — so a session lasted one access-token lifetime and
// then had nothing left to authenticate with: v2 API calls went out bare and the backend
// answered "Missing Authorization header", while Graph rejected the dead access token as
// "Invalid token lifetime" (WIPE-946). With the code flow the session renews silently for the
// refresh token's lifetime, and interactive recovery is needed only once that too is gone.
import { CapacitorHttp } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { isNative, platform } from '../capacitor';
import { captureError } from './posthog';

// The whole session under one key, in the platform keystore (Android Keystore / iOS Keychain)
// rather than localStorage, because a refresh token is a long-lived credential — unlike the
// access and ID tokens it mints, which are worth minutes.
const SESSION_KEY = 'we_ms_native_session';

// The implicit flow's storage, kept only to carry an already-signed-in user across the upgrade;
// see adoptLegacySession. Nothing writes these any more.
const LEGACY_ID_TOKEN_KEY = 'cap_ms_id_token';
const LEGACY_ACCESS_TOKEN_KEY = 'cap_ms_access_token';
const LEGACY_EXPIRES_AT_KEY = 'cap_ms_access_expires_at';

// The authorize round-trip's own state, which has to survive it: on iOS the navigation tears the
// page down, and on Android the Custom Tab can outlive a backgrounded app. localStorage rather
// than the keystore because none of it is a credential — the verifier's job is to prove the app
// that redeems the code is the one that asked for it, and it is single-use either way.
const STATE_KEY = 'cap_oauth_state';
const NONCE_KEY = 'cap_oauth_nonce';
const VERIFIER_KEY = 'cap_oauth_verifier';

// Renew a session that expires within the next two minutes rather than one already dead, so a
// token that passes the check can't expire in flight, and a device whose clock runs ahead of
// Entra's still renews before its tokens are refused.
const REFRESH_SKEW_MS = 120_000;

// Identity scopes get us an ID token whose `aud` is our own client id, which is what the v2
// backend verifies; the Graph scopes get an access token the calendar can use. One request
// returns both, plus the refresh token that renews them. Calendars.ReadWrite is here so calendar
// access needs no second consent (WIPE-1279).
const SCOPE = 'User.Read Calendars.ReadWrite openid profile email offline_access';

interface NativeMsSession {
  idToken: string;
  accessToken: string;
  // Absent only for a session adopted from the implicit flow, which had no refresh token. Such
  // a session cannot be renewed and is retired at its first expiry, in favour of a code-flow one.
  refreshToken?: string;
  // Absolute expiry of the pair, in ms. The earlier of the access token's stated lifetime and
  // the ID token's own `exp`, since both have to be live for the session to be usable.
  expiresAt: number;
}

function clientId(): string {
  return import.meta.env.VITE_MICROSOFT_CLIENT_ID || '';
}

function authority(): string {
  return import.meta.env.VITE_MICROSOFT_AUTHORITY || 'https://login.microsoftonline.com/common';
}

// iOS uses the capacitor:// scheme; Android uses a msauth:// custom scheme registered via
// intent-filter in AndroidManifest.xml so the redirect deep-links back into the app.
function redirectUri(): string {
  return platform === 'android'
    ? 'msauth://com.weinsure.agencyos/callback'
    : 'capacitor://localhost/microsoft/callback';
}

// ---------------------------------------------------------------------------
// Session storage
// ---------------------------------------------------------------------------

// The keystore is async, and reading it on every API call would put a bridge round-trip in front
// of every request. Cached in memory after the first read; `loading` collapses the concurrent
// first reads a page load fires into one. `loaded` is separate from a null `cached` so that
// "there is no session" is remembered too — otherwise an Okta-only user on native would pay a
// keystore miss on every single request.
let cached: NativeMsSession | null = null;
let loaded = false;
let loading: Promise<NativeMsSession | null> | null = null;

async function readSession(): Promise<NativeMsSession | null> {
  if (!isNative) return null;
  if (loaded) return cached;
  if (!loading) loading = loadSession().finally(() => { loading = null; });
  return loading;
}

async function loadSession(): Promise<NativeMsSession | null> {
  try {
    const { value } = await SecureStoragePlugin.get({ key: SESSION_KEY });
    cached = value ? (JSON.parse(value) as NativeMsSession) : null;
  } catch {
    // The plugin rejects rather than returning null for a key it doesn't hold, so this is the
    // ordinary "no session" path as well as the error one.
    cached = null;
  }
  if (!cached) adoptLegacySession();
  loaded = true;
  return cached;
}

async function writeSession(session: NativeMsSession): Promise<void> {
  cached = session;
  loaded = true;
  try {
    await SecureStoragePlugin.set({ key: SESSION_KEY, value: JSON.stringify(session) });
  } catch (error) {
    // Keep the in-memory copy: the session then lasts as long as this page does instead of
    // failing the sign-in outright.
    captureError(error, { component: 'nativeMsAuth.writeSession', platform });
  }
}

// Carry a user who signed in under the implicit flow across the upgrade, instead of signing
// everyone out on the release that ships the code flow. The adopted tokens work until they
// expire; with no refresh token there is nothing to renew, so the first expiry takes the normal
// recovery path and re-establishes the session through the code flow.
function adoptLegacySession(): NativeMsSession | null {
  const idToken = readLocal(LEGACY_ID_TOKEN_KEY);
  const accessToken = readLocal(LEGACY_ACCESS_TOKEN_KEY);
  if (!idToken && !accessToken) return null;

  const stored = Number(readLocal(LEGACY_EXPIRES_AT_KEY));
  const accessExpiresAt = Number.isFinite(stored) && stored > 0 ? stored : jwtExpiryMs(accessToken ?? '');
  const idExpiresAt = jwtExpiryMs(idToken ?? '');

  cached = {
    idToken: idToken ?? '',
    accessToken: accessToken ?? '',
    // Adopted as already expired when either token is missing or undateable. The ID token in
    // particular is what the v2 backend verifies, and the build before this one discarded it —
    // so a session left by that build holds a Graph access token and nothing that can
    // authenticate an API call. Presenting that as live is the production failure being fixed
    // here; reporting it expired instead is what makes the app re-establish it through the code
    // flow, while hasStoredNativeMicrosoftSession still distinguishes it from a stranger.
    expiresAt: idToken && accessToken ? Math.min(accessExpiresAt ?? 0, idExpiresAt ?? 0) : 0,
  };
  return cached;
}

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function clearLocal(...keys: string[]): void {
  try {
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    // Ignore storage errors.
  }
}

// ---------------------------------------------------------------------------
// PKCE
// ---------------------------------------------------------------------------

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// 32 random bytes → a 43-character verifier, the shortest RFC 7636 allows.
function createVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

async function challengeFor(verifier: string): Promise<string> {
  // The app runs on whatever Android System WebView the device has, and old ones have already
  // cost us crypto.randomUUID (WIPE-1312). SubtleCrypto.digest predates that by years, so this
  // should not fire — but a downgrade to code_challenge_method=plain on exactly the devices
  // least able to protect a verifier is the wrong trade, so refuse instead.
  if (!crypto.subtle?.digest) {
    throw new Error('This device cannot sign in securely: its browser engine is missing SHA-256 support.');
  }
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

// The `exp` claim, in ms, or null for anything that isn't a decodable JWT with a numeric exp.
function jwtExpiryMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json)?.exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

// The claims the app shows the user. Read from the ID token, which is ours to inspect.
export interface NativeMsClaims {
  name: string;
  email: string;
}

export function claimsFromIdToken(idToken: string): NativeMsClaims {
  const payload = JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  return {
    name: payload.name || payload.preferred_username || '',
    email: payload.preferred_username || payload.email || '',
  };
}

// ---------------------------------------------------------------------------
// Token endpoint
// ---------------------------------------------------------------------------

interface TokenResponse {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

// Over CapacitorHttp so the request is made natively and never meets a CORS check; see the
// module header. The client is public, so there is no secret in the body — PKCE is what binds
// the code to this app.
async function postToTokenEndpoint(form: Record<string, string>): Promise<NativeMsSession> {
  const response = await CapacitorHttp.post({
    url: `${authority()}/oauth2/v2.0/token`,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    data: new URLSearchParams(form).toString(),
  });

  // CapacitorHttp parses a JSON content-type for us but hands back a string otherwise, and an
  // Entra error still carries the JSON body that says what went wrong.
  const body: TokenResponse = typeof response.data === 'string'
    ? JSON.parse(response.data || '{}')
    : (response.data ?? {});

  if (response.status < 200 || response.status >= 300 || body.error) {
    throw new Error(`Microsoft token request failed (${response.status}): ${body.error_description || body.error || 'unknown error'}`);
  }
  if (!body.id_token || !body.access_token) {
    throw new Error('Microsoft token response carried no tokens.');
  }

  const accessExpiresAt = body.expires_in && body.expires_in > 0
    ? Date.now() + body.expires_in * 1000
    : jwtExpiryMs(body.access_token);
  const idExpiresAt = jwtExpiryMs(body.id_token);

  return {
    idToken: body.id_token,
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    // Whichever dies first governs, since the session needs both. An unknown lifetime falls back
    // to the other token's, and to one hour if neither could be read — short enough that a
    // wrong guess costs one silent renewal rather than a failed request.
    expiresAt: Math.min(accessExpiresAt ?? idExpiresAt ?? 0, idExpiresAt ?? accessExpiresAt ?? 0)
      || Date.now() + 3_600_000,
  };
}

// ---------------------------------------------------------------------------
// Renewal
// ---------------------------------------------------------------------------

// Concurrent API calls all find the same expired session, and each would otherwise spend a
// refresh token that Entra rotates on use — the second request would present one already
// replaced. One renewal at a time, shared by everyone waiting on it.
let refreshing: Promise<NativeMsSession | null> | null = null;

async function renew(session: NativeMsSession): Promise<NativeMsSession | null> {
  if (!session.refreshToken) return null;
  if (!refreshing) {
    refreshing = refreshWith(session.refreshToken).finally(() => { refreshing = null; });
  }
  return refreshing;
}

async function refreshWith(refreshToken: string): Promise<NativeMsSession | null> {
  try {
    const renewed = await postToTokenEndpoint({
      client_id: clientId(),
      scope: SCOPE,
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    });
    // Entra rotates the refresh token, but only returns a new one when it chooses to; keep the
    // one we used when it doesn't, or the session would lose its ability to renew again.
    if (!renewed.refreshToken) renewed.refreshToken = refreshToken;
    await writeSession(renewed);
    return renewed;
  } catch (error) {
    // The refresh token is spent, revoked or outside its lifetime. Retire just the refresh token
    // and keep the session: the caller then gets no token, which is what triggers interactive
    // recovery, while the session's continued existence is the signal that recovery is warranted
    // at all rather than the user never having signed in.
    //
    // Dropping the token matters beyond tidiness. Without it, every subsequent API call would
    // find the session expired, retry the same rejected token and spend a network request doing
    // it — a request per call, for as long as the app stayed open.
    if (cached?.refreshToken === refreshToken) {
      await writeSession({ ...cached, refreshToken: undefined });
    }
    captureError(error, { component: 'nativeMsAuth.refresh', platform });
    return null;
  }
}

// The session to use right now: the stored one while it is live, a renewed one when it isn't,
// and null when renewal is impossible or failed.
async function freshSession(): Promise<NativeMsSession | null> {
  const session = await readSession();
  if (!session) return null;
  if (Date.now() + REFRESH_SKEW_MS < session.expiresAt) return session;
  return renew(session);
}

// ---------------------------------------------------------------------------
// Reading the session
// ---------------------------------------------------------------------------

// The ID token the v2 backend verifies, renewing first if needed. Null means the session can no
// longer produce one, which apiAuth answers with interactive recovery.
export async function getNativeMicrosoftIdToken(): Promise<string | null> {
  return (await freshSession())?.idToken ?? null;
}

// The Graph access token for calendar calls, renewing first if needed.
export async function getNativeMicrosoftAccessToken(): Promise<string | null> {
  return (await freshSession())?.accessToken ?? null;
}

// Whether this device ever completed a native Microsoft login, live or not. The distinction
// recovery turns on: a session to re-authenticate, versus a visitor who was never signed in and
// should be left alone.
export async function hasStoredNativeMicrosoftSession(): Promise<boolean> {
  return !!(await readSession());
}

// Whether the session can still authenticate a request, renewing to find out.
export async function hasLiveNativeMicrosoftSession(): Promise<boolean> {
  return !!(await freshSession());
}

export async function clearNativeMicrosoftTokens(): Promise<void> {
  cached = null;
  // Known-empty, not merely unread, so later calls answer from memory instead of the keystore.
  loaded = true;
  clearLocal(LEGACY_ID_TOKEN_KEY, LEGACY_ACCESS_TOKEN_KEY, LEGACY_EXPIRES_AT_KEY, STATE_KEY, NONCE_KEY, VERIFIER_KEY);
  if (!isNative) return;
  try {
    await SecureStoragePlugin.remove({ key: SESSION_KEY });
  } catch {
    // Nothing stored, or the keystore is unavailable — the in-memory copy is already gone.
  }
}

// ---------------------------------------------------------------------------
// Sign-in
// ---------------------------------------------------------------------------

// Start the native Microsoft login: authorization code + PKCE, with the response in the fragment
// because the hash survives the redirect back into the app (capacitor.ts `appUrlOpen` forwards
// it to /microsoft/callback).
//
// `promptSelectAccount` distinguishes the two callers: a login the user asked for shows the
// account picker, while re-authenticating an expired session omits `prompt` so Entra can
// complete it against its own session cookie without the user choosing anything.
export async function startNativeMicrosoftLogin({ promptSelectAccount = true } = {}): Promise<void> {
  const state = crypto.randomUUID();
  const nonce = crypto.randomUUID();
  const verifier = createVerifier();
  const challenge = await challengeFor(verifier);

  try {
    localStorage.setItem(STATE_KEY, state);
    localStorage.setItem(NONCE_KEY, nonce);
    localStorage.setItem(VERIFIER_KEY, verifier);
  } catch {
    // Without the verifier the code cannot be redeemed and without the state the response
    // cannot be checked, so fail here rather than send the user on a round-trip that can't land.
    throw new Error('Cannot start Microsoft sign-in: browser storage is unavailable.');
  }

  const params = new URLSearchParams({
    client_id: clientId(),
    response_type: 'code',
    redirect_uri: redirectUri(),
    scope: SCOPE,
    response_mode: 'fragment',
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    ...(promptSelectAccount ? { prompt: 'select_account' } : {}),
  });
  const authUrl = `${authority()}/oauth2/v2.0/authorize?${params}`;

  // Android: open in a Chrome Custom Tab. If we used window.location.href, Microsoft would load
  // inside the app's own WebView (which runs on https://localhost/) and destroy the React app +
  // its appUrlOpen listener, so when Microsoft redirects to msauth://..., Android relaunches the
  // app but nothing is listening for the callback. The Custom Tab keeps the app alive in the
  // background; the msauth:// intent brings it to the foreground with listeners still attached.
  //
  // iOS: capacitor:// URLs are served by the app's own WKWebView, so a plain navigation keeps the
  // React app alive and the WebView catches the redirect. Using Browser.open on iOS would spawn a
  // SafariViewController that can't send the response back into the app.
  if (platform === 'android') {
    await Browser.open({ url: authUrl });
  } else {
    window.location.href = authUrl;
  }
}

// Fire-and-forget variant for the background recovery path in apiAuth, which has no user
// interaction to report a failure to. Resolves to whether the round-trip actually started, so the
// caller can clear its in-progress flag and let a later call try again.
export async function startNativeMicrosoftReauth(): Promise<boolean> {
  try {
    await startNativeMicrosoftLogin({ promptSelectAccount: false });
    return true;
  } catch (error) {
    captureError(error, { component: 'nativeMsAuth.startNativeMicrosoftReauth', platform });
    return false;
  }
}

// ---------------------------------------------------------------------------
// Completing sign-in
// ---------------------------------------------------------------------------

// Whether a fragment is a response to the flow above, and so ours to complete rather than
// MSAL's. Matches an error response too: Entra reports a refused authorize in the same place, and
// treating it as "not for us" would leave the user on a blank callback screen.
export function isNativeMicrosoftAuthResponse(hash: string): boolean {
  if (!isNative || !hash) return false;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return params.has('code') || params.has('error');
}

// Redeem the authorization code and store the session. Returns the signed-in user's claims, or
// null when the response isn't one we can act on — a state mismatch (a stale or forged
// redirect), or an error response from Entra.
export async function completeNativeMicrosoftLogin(hash: string): Promise<NativeMsClaims | null> {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const code = params.get('code');
  const state = params.get('state');
  const verifier = readLocal(VERIFIER_KEY);
  const expectedState = readLocal(STATE_KEY);

  // Consume the single-use round-trip state whatever the outcome, so a failed attempt can't be
  // replayed and a stale verifier can't be spent on a later response.
  clearLocal(STATE_KEY, NONCE_KEY, VERIFIER_KEY);

  const error = params.get('error');
  if (error) {
    throw new Error(`Microsoft sign-in was refused: ${params.get('error_description') || error}`);
  }
  if (!code || !state || !expectedState || state !== expectedState || !verifier) {
    // Not our response, or one we can't prove is ours. Nothing to report to Entra; the caller
    // renders the login screen again.
    return null;
  }

  const session = await postToTokenEndpoint({
    client_id: clientId(),
    scope: SCOPE,
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });
  await writeSession(session);
  // The implicit flow's leftovers would otherwise be adopted as a legacy session on some later
  // read, overriding the one just established.
  clearLocal(LEGACY_ID_TOKEN_KEY, LEGACY_ACCESS_TOKEN_KEY, LEGACY_EXPIRES_AT_KEY);

  return claimsFromIdToken(session.idToken);
}
